<template>
    <div class="documents-layout" :class="{ 'sidebar-collapsed': !isSidebarVisible }">
        <!-- Sidebar for Filters or Details -->
        <aside class="filters-sidebar">
            <SearchFilters v-show="isActiveSearchTab" @clicked="onClickChild" @close-sidebar="isSidebarVisible = false"
                ref="searchFiltersRef" />
            <DocumentDetails v-if="activeDocumentTab && activeDocumentTab.Document"
                :document="activeDocumentTab.Document" @close-sidebar="isSidebarVisible = false"
                @refresh-document="refreshDocumentData($event)" />
        </aside>

        <!-- Main Content Area -->
        <main class="content-area">
            <div class="main-view-container">
                <div class="subtabs-wrapper">
                    <DxTabPanel :data-source="allTabs" :selected-index.sync="activeSubTab"
                        @option-changed="onSubTabChanged" height="100%" class="subtabs-panel" :scrolling-enabled="true"
                        :show-nav-buttons="true">
                        <template #title="{ data, index }">
                            <div v-if="data && data.title" class="tab-title-container">
                                <span>{{ data.title }}</span>
                                <DxButton v-if="data.tabType === 'document'" icon="close" text="" styling-mode="text"
                                    @click="closeSubTab(index)" />
                            </div>
                        </template>
                        <template #item="{ data }">
                            <div v-if="data.tabType === 'search' && data.searchTarget === 'documente'" class="list-view-content">
                                <DocumenteView :initial-filters="sharedSearchParams" @select-document="openDocumentDetailTab" @toggle-filters="toggleSidebar($event)" ref="documenteViewRef"/>
                            </div>
                            <div v-else-if="data.tabType === 'search' && data.searchTarget === 'linii'" class="list-view-content">
                                <LiniiDocumenteView :initial-filters="sharedSearchParams" @open-document="openDocumentDetailTab" @toggle-filters="toggleSidebar($event)" ref="liniiViewRef"/>
                            </div>
                            <div v-else class="line-view-content">
                                <LiniView v-if="data.Document" :document="data.Document" @toggle-details="isSidebarVisible = !isSidebarVisible" @refresh-document="refreshDocumentData($event)"/>
                            </div>
                        </template>
                    </DxTabPanel>
                </div>
            </div>
        </main>
    </div>
</template>
<script lang="ts">
import DocumenteView from './DocumenteView.vue';
import LiniiDocumenteView from './LiniiDocumenteView.vue';
import LiniView from './LiniView.vue';
import SearchFilters from './SearchFilters.vue'
import DocumentDetails from './DocumentDetails.vue'
import { DxButton } from 'devextreme-vue/button';
import { DxTabs } from 'devextreme-vue/tabs';
import AppSetting from '@/settings/appSettings';
import DataSource from "devextreme/data/data_source";
import notify from 'devextreme/ui/notify'
import { DxTabPanel } from 'devextreme-vue/tab-panel';
import { defineComponent } from 'vue';

type SearchTarget = 'documente' | 'linii'

interface TabItem {
    id: string,
    title: string,
    tabType: 'search' |'document',
    searchTarget?: SearchTarget,
    documentId?: string,
    Document?: any,
}

export default defineComponent({

    name: 'Documente',
    components: {
        LiniView,
        DocumenteView,
        SearchFilters,
        DocumentDetails,
        DxTabs,
        DxTabPanel,
        DxButton,
        LiniiDocumenteView
    },
    data() {
        return {
            currentTab: 'Documente',
            params: null as any,
            sharedSearchParams: null as any,
            openDocuments: [] as TabItem[],
            activeSubTab: 0,
            uniqueTabCounter: 0,
            isSidebarVisible: true
        }
    },
    computed: {
        allTabs(): TabItem[] {
            return [
                { id: 'documents-search-tab', title: 'Documente', tabType: 'search', searchTarget: 'documente', documentId: '' },
                { id: 'lines-search-tab', title: 'Linii Documente', tabType: 'search', searchTarget: 'linii', documentId: '' },
                ...this.openDocuments
            ];
        },
        activeTab(): TabItem | null {
            return this.allTabs[this.activeSubTab] ?? null
        },
        isActiveSearchTab(): boolean {
            return this.activeTab?.tabType === 'search'
        },
        activeSearchTarget(): SearchTarget | null {
            if (this.activeTab?.tabType !== 'search') {
                return null
            }

            return this.activeTab?.searchTarget ?? null
        },
        activeDocumentTab(): TabItem | null {
            if (this.activeTab?.tabType !== 'document') {
                return null
            }

            return this.activeTab
        } 
    },
    async mounted() {
        const storedDocs = localStorage.getItem('openDocuments')
        const storedActiveSubTab = localStorage.getItem('activeSubTab')

        if (storedDocs && storedDocs !== 'undefined') {
            const simplifiedTabs = JSON.parse(storedDocs) as Pick<TabItem, 'id' | 'documentId' | 'title'>[]

            this.openDocuments = []
            await this.loadDocumentsForStoredTabs(simplifiedTabs)
        }
        if (storedActiveSubTab && storedActiveSubTab !== 'undefined') {
            const parsedIndex = Number(storedActiveSubTab)
            this.activeSubTab = !isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < this.allTabs.length ? parsedIndex : 0
        }

        const storedSidebarVisible = localStorage.getItem('isSidebarVisible')
        if (storedSidebarVisible !== null) this.isSidebarVisible = storedSidebarVisible === 'true'

        const rawRouteId = this.$route.query.id
        const documentIdFromRoute =  Array.isArray(rawRouteId) ? rawRouteId[0] : rawRouteId
        if (documentIdFromRoute) {
            try {
                const doc = await this.fetchDocument(String(documentIdFromRoute))
                if (doc) {
                    await this.openDocumentDetailTab(doc)
                } else {
                    notify("Eroare la găsirea documentului", 'error', 5000)
                }
            } catch (e) {
                console.error("Eroare la încărcarea documentului din URL:", e);
                notify("A apÄƒrut o eroare la deschiderea documentului.", "error", 5000);
            }

            this.$router.replace({ path: this.$route.path, query: {} }).catch(() => { });
        }
    },
    methods: {
        getSingleRef(refName: string) {
            const ref = this.$refs[refName] as any;
            return Array.isArray(ref) ? ref[0] : ref;
        },
        async fetchDocument(documentId: string): Promise<any | null> {
            if (!documentId) return null

            try {
                const documents = new DataSource({
                    store: {
                        type: 'odata',
                        url: AppSetting.API_Url_DocumenteOdata + 'DOC_CautareDocumente',
                        key: 'Id',
                        keyType: "Guid",
                        version: 4,
                        beforeSend: (e: any) => {
                            e.headers = {
                                "Content-Type": "application/json",
                                "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                            };
                        },
                    },
                } as any)

                documents.filter(['Id', '=', documentId])

                const result = await documents.load()

                if (!Array.isArray(result) || result.length === 0) {
                    return null
                }

                return result[0]
            } catch (error) {
                console.error(`Eroare la încărcarea documentului ${documentId}:`, error)
                return null
            }
        },
        async loadDocumentsForStoredTabs(simplifiedTabs: Pick<TabItem, 'id' | 'documentId' | 'title'>[]) {
            const promises = simplifiedTabs.map(async tab => {
                try {
                    const document = await this.fetchDocument(tab.documentId || '');

                    if (!document) return null

                    return {
                        ...tab,
                        tabType: 'document' as const,
                        Document: this.normalizeDocument(document)
                    } as TabItem;
                } catch (error) {
                    console.error(`Error loading document ${tab.documentId}:`, error);
                    return null;
                }
            });

            const loadedTabs = (await Promise.all(promises)).filter(tab => tab !== null) as TabItem[];
            this.openDocuments = loadedTabs;
        },
        async refreshDocumentData(documentId: string) {
            const fullDoc = await this.fetchDocument(documentId);
            if (fullDoc) {
                const tab = this.openDocuments.find(t => String(t.documentId).toLowerCase() === String(documentId).toLowerCase());
                if (tab) {
                    tab.Document = this.normalizeDocument(fullDoc, tab.Document);
                }
            }
        },
        onSubTabChanged(e: any) {
            if (e.name !== 'selectedIndex') {
                return;
            }

            this.activeSubTab = e.component.option('selectedIndex');
        },
        onClickChild(value) {
            const safeValue = value ?? null
            this.params = safeValue
            this.sharedSearchParams = safeValue
            this.isSidebarVisible = false
        },
        async openDocumentDetailTab(document) {
            const selectedDocumentId = document.Id;

            if (!selectedDocumentId) {
                notify("Documentul selectat nu are ID valid.", "error", 5000)
                return
            }

            // Always fetch the richest data from the search service
            const fullDocRaw = await this.fetchDocument(String(selectedDocumentId));
            if (!fullDocRaw) {
                notify("Nu s-au putut încărca detaliile complete ale documentului.", "error", 5000);
                return;
            }

            const fullDoc = this.normalizeDocument(fullDocRaw, document)
            const existing = this.openDocuments.find(t => String(t.documentId).toLowerCase() === String(fullDoc.Id).toLowerCase())

            if (existing) {
                existing.Document = fullDoc; // Refresh data
                const actualIndexInAllTabs = this.allTabs.indexOf(existing);
                if (actualIndexInAllTabs !== -1) {
                    this.activeSubTab = actualIndexInAllTabs;
                }

                return
            }

            this.uniqueTabCounter++

            const newTab: TabItem = {
                id: `${fullDoc.Numar} - ${this.uniqueTabCounter}`,
                documentId: fullDoc.Id,
                title: `${fullDoc.Numar} - ${fullDoc.Tip || 'Document'}`,
                Document: fullDoc,
                tabType: 'document'
            }

            this.openDocuments.push(newTab);
            this.$nextTick(() => {
                this.activeSubTab = this.allTabs.length - 1;
            });
        },
        normalizeDocument (fullDoc: any, fallback: any = {}) {
            const getID = (obj: any) => obj ? (obj.Id || obj.ID || null) : null;
            const getString = (val: any) => (val && typeof val === 'object') ? (val.Nume || val.Denumire || val.Simbol || "") : val;

            // Merge/Normalize properties
            fullDoc.PartenerId = fullDoc.PartenerId || fullDoc.PartenerID || getID(fullDoc.Partener) || fallback.PartenerId || fallback.PartenerID;
            fullDoc.ContractId = fullDoc.ContractId || fullDoc.ContractID || getID(fullDoc.Contract) || fallback.ContractId || fallback.ContractID;
            fullDoc.ProiectId = fullDoc.ProiectId || fullDoc.ProiectID || getID(fullDoc.Proiect) || fallback.ProiectId || fallback.ProiectID;
            fullDoc.GestiuneIntrareId = fullDoc.GestiuneIntrareId || fullDoc.GestiuneIntrareID || getID(fullDoc.GestiuneIntrare) || fallback.GestiuneIntrareId || fallback.GestiuneIntrareID;
            fullDoc.GestiuneIesireId = fullDoc.GestiuneIesireId || fullDoc.GestiuneIesireID || getID(fullDoc.GestiuneIesire) || fallback.GestiuneIesireId || fallback.GestiuneIesireID;
            fullDoc.PretValutaId = fullDoc.PretValutaId || fullDoc.PretValutaID || getID(fullDoc.PretValuta) || fallback.PretValutaId || fallback.PretValutaID;
            fullDoc.ValutaId = fullDoc.ValutaId || fullDoc.ValutaID || getID(fullDoc.Valuta) || fallback.ValutaId || fallback.ValutaID;
            fullDoc.StructIdEditabil = fullDoc.StructIdEditabil || fullDoc.StructIDEditabil || getID(fullDoc.StructuraCompanie) || fallback.StructIdEditabil || fallback.StructIDEditabil;
            fullDoc.TipId = fullDoc.TipId || fullDoc.TipID || getID(fullDoc.Tip) || fallback.TipId || fallback.TipID;
            fullDoc.CategorieId = fullDoc.CategorieId || fullDoc.CategorieID || getID(fullDoc.Categorie) || fallback.CategorieId || fallback.CategorieID;
            fullDoc.AgentId = fullDoc.AgentId || fullDoc.AgentID || getID(fullDoc.Agent) || fallback.AgentId || fallback.AgentID;
            fullDoc.ZonaId = fullDoc.ZonaId || fullDoc.ZonaID || getID(fullDoc.Zona) || fallback.ZonaId || fallback.ZonaID;
            fullDoc.ComandaId = fullDoc.ComandaId || fullDoc.ComandaID || getID(fullDoc.Comanda) || fallback.ComandaId || fallback.ComandaID;

            fullDoc.Partener = getString(fullDoc.Partener) || getString(fallback.Partener);
            fullDoc.Tip = getString(fullDoc.Tip) || getString(fallback.Tip);
            fullDoc.Proiect = getString(fullDoc.Proiect) || getString(fallback.Proiect);
            fullDoc.Categorie = getString(fullDoc.Categorie) || getString(fallback.Categorie);
            fullDoc.Valuta = getString(fullDoc.Valuta) || getString(fallback.Valuta);
            fullDoc.Zona = getString(fullDoc.Zona) || getString(fallback.Zona);
            fullDoc.GestiuneIntrare = getString(fullDoc.GestiuneIntrare) || getString(fallback.GestiuneIntrare);
            fullDoc.GestiuneIesire = getString(fullDoc.GestiuneIesire) || getString(fallback.GestiuneIesire);

            fullDoc.Tva = fullDoc.Tva !== undefined ? fullDoc.Tva : (fullDoc.TVA !== undefined ? fullDoc.TVA : (fallback.Tva !== undefined ? fallback.Tva : fallback.TVA));
            fullDoc.ValoareTva = fullDoc.ValoareTva !== undefined ? fullDoc.ValoareTva : (fullDoc.ValoareTVA !== undefined ? fullDoc.ValoareTVA : (fallback.ValoareTva !== undefined ? fallback.ValoareTva : fallback.ValoareTVA));
            fullDoc.PretCurs = fullDoc.PretCurs !== undefined ? fullDoc.PretCurs : (fullDoc.Curs !== undefined ? fullDoc.Curs : fallback.PretCurs);
            fullDoc.Curs = fullDoc.Curs !== undefined ? fullDoc.Curs : (fullDoc.PretCurs !== undefined ? fullDoc.PretCurs : fallback.Curs);

            if (fullDoc.Contract && typeof fullDoc.Contract === 'object') {
                fullDoc.ContractNumar = fullDoc.Contract.NumarContract || fullDoc.Contract.Numar;
            }
            fullDoc.ContractNumar = fullDoc.ContractNumar || fallback.ContractNumar || fallback.NumarContract;

            return fullDoc
        },
        closeSubTab(index: number) {
            const firstDynamicTabIndex = 2
            const openDocsIndex = index - firstDynamicTabIndex

            if (openDocsIndex < 0) {
                return
            }

            const closedWasActive = this.activeSubTab === index
            const closedWasBeforeActive = index < this.activeSubTab

            this.openDocuments.splice(openDocsIndex, 1)

            if (closedWasActive) {
                this.activeSubTab = Math.max(0, index - 1)
            } else if (closedWasBeforeActive) {
                this.activeSubTab = this.activeSubTab - 1
            }
        },
        toggleSidebar(section?: string) {
            this.isSidebarVisible = !this.isSidebarVisible;
            if (this.isSidebarVisible && section) {
                this.$nextTick(() => {
                    const searchFiltersRef = this.getSingleRef('searchFiltersRef');
                    searchFiltersRef?.openSection?.(section);
                });
            } else if (this.isSidebarVisible) {
                this.$nextTick(() => {
                    const searchFiltersRef = this.getSingleRef('searchFiltersRef');
                    searchFiltersRef?.openSection?.('document');
                });
            }
        },
    },
    watch: {
        openDocuments: {
            handler(newDocs: TabItem[]) {
                const simplifiedDocsToStore = newDocs.map(doc => ({
                    id: doc.id,
                    documentId: doc.documentId,
                    title: doc.title
                }))

                localStorage.setItem('openDocuments', JSON.stringify(simplifiedDocsToStore))
            }, deep: true
        },
        activeSubTab(newVal) {
            localStorage.setItem('activeSubTab', newVal)
        },
        isSidebarVisible(newVal) {
            localStorage.setItem('isSidebarVisible', newVal)
        }
    },
})
</script>
<style scoped>
.documents-layout {
    display: flex;
    height: calc(100vh - 64px);
    background-color: #f8f9fa;
    overflow: hidden;
    position: relative;
}

.filters-sidebar {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 400px !important;
    min-width: 400px !important;
    max-width: 400px !important;
    z-index: 1050;
    border-left: 1px solid #e2e8f0;
    background: white;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0;
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-sizing: border-box;
}

.sidebar-collapsed .filters-sidebar {
    transform: translateX(100%);
}

.content-area {
    flex-grow: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
}


.main-view-container {
    padding: 24px;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.subtabs-wrapper {
    flex-grow: 1;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.list-view-content {
    padding: 16px;
    height: 100%;
    position: relative;
}

.line-view-content {
    padding: 16px;
    height: 100%;
}

.no-docs-message {
    margin-top: 40px;
    text-align: center;
    color: #64748b;
}

.subtabs-panel .dx-tab {
    flex-shrink: 0;
    min-width: 200px !important;
    max-width: none;
}

.subtabs-panel .dx-tab .tab-title-container {
    display: flex;
    align-items: center;
    width: 100%;
}

.subtabs-panel .dx-tab .tab-title-container span {
    white-space: nowrap;
    flex-grow: 1;
    overflow: hidden;
    text-overflow: ellipsis;
}

.subtabs-panel .dx-tab .tab-title-container .dx-button {
    flex-shrink: 0;
    margin-left: 8px;
}

@media (max-width: 768px) {
    .main-view-container {
        padding: 10px;
    }
    .filters-sidebar {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 100% !important;
    }
    .list-view-content {
        padding: 8px;
    }
}
</style>
